import React, { forwardRef } from 'react';

// ===== Helpers =====
const fmt = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatGregorian = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const part = new Intl.DateTimeFormat('en-CA', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      timeZone: 'Asia/Dubai',
    }).format(d);
    const [y, m, dd] = part.split('-');
    const time = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai', hour12: false,
    }).format(d);
    return `${dd}/${m}/${y} - ${time}`;
  } catch {
    return '';
  }
};

const formatHijri = (iso) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
      day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Asia/Dubai',
    }).format(new Date(iso));
  } catch {
    return '';
  }
};

const monthLabel = (ym) => {
  if (!ym) return '';
  try {
    const [y, m] = ym.split('-');
    const d = new Date(Number(y), Number(m) - 1, 1);
    return new Intl.DateTimeFormat('ar-EG', { month: 'long', year: 'numeric' }).format(d);
  } catch {
    return ym;
  }
};

// ===== Sub-components =====

const StatementHeader = () => (
  <div style={{ borderBottom: '2px solid #c9a253', paddingBottom: '8mm', marginBottom: '8mm' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ fontSize: '20pt', fontWeight: 800, color: '#0f0f0f' }}>KE Gents Saloon</div>
        <div style={{ fontSize: '11pt', color: '#666', marginTop: '2mm' }}>صالون الرجال المتميز</div>
      </div>
      <div style={{ textAlign: 'left', fontSize: '11pt', color: '#444' }}>
        <div style={{ fontWeight: 700, color: '#c9a253' }}>كشف حساب</div>
        <div style={{ marginTop: '2mm' }}>Statement of Account</div>
      </div>
    </div>
  </div>
);

const BarberMonthInfo = ({ barberName, month }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4mm', marginBottom: '8mm', fontSize: '12pt' }}>
    <div>
      <div style={{ color: '#888', fontSize: '10pt' }}>اسم الحلاق</div>
      <div style={{ fontWeight: 700, fontSize: '14pt', marginTop: '1mm' }}>{barberName}</div>
    </div>
    <div style={{ textAlign: 'left' }}>
      <div style={{ color: '#888', fontSize: '10pt' }}>الشهر</div>
      <div style={{ fontWeight: 700, fontSize: '14pt', marginTop: '1mm' }}>{monthLabel(month)}</div>
    </div>
  </div>
);

const BalanceBlock = ({ netPayable, isOwed, salaryStatus, prevDeficit }) => (
  <div
    style={{
      border: '2px solid #c9a253', borderRadius: '6mm', padding: '8mm', marginBottom: '8mm',
      background: 'linear-gradient(180deg, #fffbea 0%, #ffffff 100%)',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6mm' }}>
      <div style={{ fontSize: '14pt', fontWeight: 700, color: '#666' }}>الرصيد المستحق هذا الشهر</div>
      <div style={{ fontSize: '10pt', color: '#888' }}>{salaryStatus}</div>
    </div>
    <div
      style={{
        fontSize: '36pt', fontWeight: 900, color: isOwed ? '#0f7a3f' : '#b91c1c',
        textAlign: 'center', letterSpacing: '0.5pt', direction: 'ltr',
      }}
    >
      {fmt(netPayable)} <span style={{ fontSize: '20pt' }}>د.إ</span>
    </div>
    {prevDeficit?.total > 0 && (
      <div
        style={{
          marginTop: '5mm', padding: '3mm 4mm', background: '#fff4e5', border: '1px solid #f0b96f',
          borderRadius: '3mm', fontSize: '11pt', color: '#a25a00', textAlign: 'center',
        }}
      >
        تم خصم العجز المتراكم من أشهر سابقة:&nbsp;
        <strong>{fmt(prevDeficit.total)} د.إ</strong>&nbsp;
        ({prevDeficit.count} شهر)
      </div>
    )}
  </div>
);

// Single row of the breakdown table. Colors picked by `tone`.
const TONE_COLOR = {
  positive: '#0f7a3f',
  negative: '#b91c1c',
  warning: '#b45309',
  neutral: undefined,
};
const BreakdownRow = ({ label, amount, tone = 'neutral', sign = '', emphasized = false, finalRow = false }) => {
  const tdBase = { padding: emphasized ? '4mm 0' : '3mm 0' };
  const labelStyle = { ...tdBase, color: emphasized ? undefined : '#666', fontWeight: emphasized ? 700 : 400 };
  const amountStyle = {
    ...tdBase, textAlign: 'left', direction: 'ltr',
    color: TONE_COLOR[tone], fontWeight: emphasized ? 700 : 400,
  };
  const trStyle = finalRow
    ? { borderTop: '2px solid #c9a253', borderBottom: '2px solid #c9a253' }
    : { borderBottom: '1px solid #eee' };
  return (
    <tr style={trStyle}>
      <td style={labelStyle}>{label}</td>
      <td style={amountStyle}>{sign}{fmt(amount)} د.إ</td>
    </tr>
  );
};

const BreakdownTable = ({ s, showSupportLine, finalLineLabel, finalLineValue }) => {
  const bonus = Number(s.salary?.bonus || 0);
  const deductions = Number(s.salary?.deductions || 0);
  const prevDeficitTotal = Number(s.previous_deficit?.total || 0);
  const supportLabel = s.salary?.absorb_deficit
    ? 'تعديل للحد الأدنى (تحمَّله الصالون)'
    : 'تعديل للحد الأدنى (يُسجَّل كعجز قادم)';

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11pt', marginBottom: '8mm' }}>
      <tbody>
        <BreakdownRow label="إجمالي إيرادات الشهر" amount={s.revenue?.total} emphasized />
        <BreakdownRow label="− حصة الالتزامات الشهرية" amount={s.commitments?.share} tone="warning" sign="−" />
        <BreakdownRow
          label={`الراتب المحسوب (${Number(s.salary?.percentage || 0)}% من الصافي)`}
          amount={s.salary?.calculated_salary}
        />
        {bonus > 0 && <BreakdownRow label="+ مكافأة" amount={bonus} tone="positive" sign="+" />}
        {deductions > 0 && <BreakdownRow label="− خصومات" amount={deductions} tone="negative" sign="−" />}
        {showSupportLine && (
          <BreakdownRow label={supportLabel} amount={s.salary?.salary_deficit} sign="+" />
        )}
        <BreakdownRow label={finalLineLabel} amount={finalLineValue} finalRow emphasized />
        {prevDeficitTotal > 0 && (
          <BreakdownRow label="− عجز متراكم من أشهر سابقة" amount={prevDeficitTotal} tone="negative" sign="−" />
        )}
      </tbody>
    </table>
  );
};

const PaymentHistoryTable = ({ history }) => {
  if (!history || history.length === 0) return null;
  return (
    <div style={{ marginBottom: '8mm' }}>
      <div style={{ fontSize: '12pt', fontWeight: 700, color: '#0f0f0f', marginBottom: '3mm' }}>
        سجل آخر 6 أشهر
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
        <thead>
          <tr style={{ background: '#f7f4ea', color: '#444' }}>
            <th style={{ padding: '2mm 3mm', textAlign: 'right', borderBottom: '1px solid #ddd' }}>الشهر</th>
            <th style={{ padding: '2mm 3mm', textAlign: 'right', borderBottom: '1px solid #ddd' }}>الراتب</th>
            <th style={{ padding: '2mm 3mm', textAlign: 'right', borderBottom: '1px solid #ddd' }}>الحالة</th>
            <th style={{ padding: '2mm 3mm', textAlign: 'right', borderBottom: '1px solid #ddd' }}>تاريخ الصرف</th>
          </tr>
        </thead>
        <tbody>
          {history.map((h) => (
            <tr key={h.month} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '2mm 3mm', direction: 'ltr' }}>{h.month}</td>
              <td style={{ padding: '2mm 3mm', direction: 'ltr' }}>{fmt(h.final_salary)} د.إ</td>
              <td style={{ padding: '2mm 3mm', color: h.status === 'paid' ? '#0f7a3f' : '#a25a00' }}>
                {h.status === 'paid' ? 'مدفوع' : 'معلّق'}
              </td>
              <td style={{ padding: '2mm 3mm', color: '#666', direction: 'ltr' }}>
                {h.paid_date
                  ? new Date(h.paid_date).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }).split('-').reverse().join('/')
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const StatementFooter = ({ gregorian, hijri }) => (
  <div
    style={{
      position: 'absolute', bottom: '14mm', left: '12mm', right: '12mm',
      fontSize: '10pt', color: '#666', borderTop: '1px solid #ddd', paddingTop: '4mm',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
      <div>
        <div>تاريخ الطباعة (ميلادي): <strong dir="ltr">{gregorian}</strong></div>
        <div style={{ marginTop: '2mm' }}>تاريخ الطباعة (هجري): <strong>{hijri} هـ</strong></div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ borderTop: '1px solid #999', width: '60mm', marginBottom: '2mm' }}></div>
        <div>توقيع الحلاق</div>
      </div>
    </div>
    <div style={{ textAlign: 'center', marginTop: '4mm', fontSize: '9pt', color: '#999' }}>
      هذا الكشف يُعتبر إقراراً برصيد الحلاق للشهر المذكور — KE Gents Saloon
    </div>
  </div>
);

// Pure derivation of payable amounts — keeps the main component skim-readable.
const computeAmounts = (s, withoutSupport) => {
  const calc = Number(s.salary?.calculated_salary || 0);
  const bonus = Number(s.salary?.bonus || 0);
  const deductions = Number(s.salary?.deductions || 0);
  const finalSalary = Number(s.salary?.final_salary || 0);
  const prevDeficit = Number(s.previous_deficit?.total || 0);
  const naturalPayable = (calc + bonus - deductions) - prevDeficit;
  return {
    netPayable: withoutSupport ? naturalPayable : Number(s.net_payable || 0),
    showSupportLine: !withoutSupport && Number(s.salary?.salary_deficit || 0) > 0,
    finalLineLabel: withoutSupport ? 'الراتب الفعلي قبل التسويات' : 'الراتب النهائي قبل التسويات',
    finalLineValue: withoutSupport ? (calc + bonus - deductions) : finalSalary,
  };
};

/**
 * Printable A4 statement of a single barber's monthly balance.
 * • Concise — only the final balance, with print date (Gregorian + Hijri).
 * • For barber printout: this is exactly what they receive.
 * • For admin: same template, but admin sees this from the multi-select report.
 */
const BarberStatementSheet = forwardRef(({ statement, withoutSupport = true }, ref) => {
  if (!statement) return null;
  const s = statement;
  const { netPayable, showSupportLine, finalLineLabel, finalLineValue } = computeAmounts(s, withoutSupport);
  const salaryStatus = s.salary?.is_persisted
    ? `حالة: ${s.salary?.status === 'paid' ? 'مدفوع' : 'معلّق'}`
    : 'لم يُحتسب بعد';

  return (
    <div
      ref={ref}
      dir="rtl"
      style={{
        width: '210mm', minHeight: '297mm', padding: '14mm 12mm',
        background: '#ffffff', color: '#1a1a1a',
        fontFamily: '"Tajawal", "Cairo", "Segoe UI", Tahoma, sans-serif',
        fontSize: '12pt', boxSizing: 'border-box',
      }}
    >
      <StatementHeader />
      <BarberMonthInfo barberName={s.barber_name} month={s.month} />
      <BalanceBlock
        netPayable={netPayable}
        isOwed={netPayable >= 0}
        salaryStatus={salaryStatus}
        prevDeficit={s.previous_deficit}
      />
      <BreakdownTable
        s={s}
        showSupportLine={showSupportLine}
        finalLineLabel={finalLineLabel}
        finalLineValue={finalLineValue}
      />
      <PaymentHistoryTable history={s.payment_history} />
      <StatementFooter gregorian={formatGregorian(s.print_date)} hijri={formatHijri(s.print_date)} />
    </div>
  );
});

BarberStatementSheet.displayName = 'BarberStatementSheet';

export default BarberStatementSheet;
